import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import HrefLangAlternates from "@/components/HrefLangAlternates";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Clock, ArrowLeft, ArrowRight, Calendar, User, Tag } from "lucide-react";

/**
 * BlogPostPage, single blog post with full SEO:
 *  - <title>, <meta description>, <link rel=canonical>
 *  - OpenGraph + Twitter card
 *  - JSON-LD Article schema (Google rich result eligibility)
 */
export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { lang } = useApp();

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const { data } = await api.get(`/blog/${slug}`);
        setPost(data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <div className="container-x py-20 text-[#A8B3AD]">Laden…</div>;
  if (notFound || !post) {
    return (
      <div className="container-x py-20 text-center">
        <h1 className="text-2xl text-white font-semibold mb-2">Artikel niet gevonden</h1>
        <Link to="/blog" className="text-[#D97742] hover:underline">← Terug naar overzicht</Link>
      </div>
    );
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const canonicalUrl = post.canonical_url || `${origin}/blog/${post.slug}`;
  const ogImage = post.og_image || post.hero_image || "";
  const fullOgImage = ogImage.startsWith("http") ? ogImage : `${origin}${ogImage}`;
  const title = post.meta_title || post.title;
  const description = post.meta_description || post.excerpt;

  // JSON-LD Article schema for Google rich results
  const articleSchema = post.structured_data || {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: fullOgImage ? [fullOgImage] : undefined,
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    author: {
      "@type": "Organization",
      name: post.author_name || "Kiosk Configurator Team",
    },
    publisher: {
      "@type": "Organization",
      name: "Kiosk Configurator",
      logo: { "@type": "ImageObject", url: `${origin}/logo192.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
    inLanguage: post.language || "nl",
    keywords: (post.tags || []).join(", "),
  };

  // BreadcrumbList schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: origin },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${origin}/blog` },
      { "@type": "ListItem", position: 3, name: post.title, item: canonicalUrl },
    ],
  };

  return (
    <article data-testid="blog-post" className="min-h-screen pb-20">
      <Helmet>
        <html lang={post.language || "nl"} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="keywords" content={(post.tags || []).join(", ")} />
        {/* OpenGraph */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        {fullOgImage && <meta property="og:image" content={fullOgImage} />}
        <meta property="article:published_time" content={post.published_at || ""} />
        <meta property="article:modified_time" content={post.updated_at || post.published_at || ""} />
        <meta property="article:author" content={post.author_name || "Kiosk Configurator Team"} />
        {(post.tags || []).map((t) => (
          <meta key={t} property="article:tag" content={t} />
        ))}
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {fullOgImage && <meta name="twitter:image" content={fullOgImage} />}
        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <HrefLangAlternates path={`/blog/${post.slug}`} />

      {/* Hero */}
      <header className="container-x pt-10 pb-8 max-w-4xl">
        <Link to="/blog" className="inline-flex items-center gap-1 text-xs text-[#A8B3AD] hover:text-white mb-6" data-testid="blog-back-link">
          <ArrowLeft className="w-3 h-3" /> Alle artikelen
        </Link>
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-[#A8B3AD] mb-3">
          {post.published_at && (
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(post.published_at).toLocaleDateString("nl-NL", { year: "numeric", month: "long", day: "numeric" })}</span>
          )}
          <span className="flex items-center gap-1"><User className="w-3 h-3" />{post.author_name || "Kiosk Configurator Team"}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.reading_time_minutes} min leestijd</span>
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-tight">
          {post.title}
        </h1>
        {post.excerpt && (
          <p className="text-base sm:text-lg text-[#A8B3AD] mt-4 leading-relaxed">{post.excerpt}</p>
        )}
        {(post.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-5">
            {post.tags.map((t) => (
              <Link key={t} to={`/blog?tag=${t}`} className="text-[10px] px-2 py-0.5 rounded-full bg-[#1F3A2E] text-[#7FD8B8] hover:bg-[#2a4a3a] transition">
                #{t}
              </Link>
            ))}
          </div>
        )}
      </header>

      {/* Hero image */}
      {post.hero_image && (
        <div className="container-x max-w-4xl mb-8">
          <div className="aspect-[16/9] bg-[#0F1816] border border-[#1F3A2E] rounded-xl overflow-hidden">
            <img src={post.hero_image} alt={post.title} className="w-full h-full object-contain" />
          </div>
        </div>
      )}

      {/* Body */}
      <section className="container-x max-w-3xl">
        <div className="prose-blog">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content_markdown}</ReactMarkdown>
        </div>
      </section>

      {/* Related */}
      {(post.related || []).length > 0 && (
        <section className="container-x max-w-5xl mt-16">
          <h2 className="text-xl font-semibold text-white mb-5">Verder lezen</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {post.related.map((r) => (
              <Link key={r.id} to={`/blog/${r.slug}`} className="card-surface p-5 hover:translate-y-[-2px] transition" data-testid={`blog-related-${r.slug}`}>
                <h3 className="text-sm font-semibold text-white">{r.title}</h3>
                <p className="text-xs text-[#A8B3AD] mt-2 line-clamp-3">{r.excerpt}</p>
                <span className="text-[11px] text-[#D97742] mt-3 inline-flex items-center gap-1">
                  Lees verder <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
